import { answerSubdomain, SkillClusterType } from '../actions/surveyActions';
import domainMapping from '../assets/page-text/DomainAndImageMapping.json';
import Answers from '../components/Answers';
import Button from '../components/Button';
import FunctionApplicationsIntroduction from '../components/FunctionalApplicationsIntroduction';
import SurveyIllustationProgress from '../components/SurveyIllustrationProgress';
import SurveyPageTemplateRoleBased from '../components/SurveyPageTemplateRoleBased';
import { validateId } from '../model/joi.models';
import { Domain, AnswerLevel, Subdomain, Id } from '../model/models';
import { State } from '../redux-state';
import { breakpoints, colors, fonts } from '../styles';
import { globalColor } from '../utils/globals';
import { authenticatedGetJson } from '../utils/requests';
import useLoadingAndError from '../utils/useLoadingAndError';
import useViewport from '../utils/useViewport';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useParams } from 'react-router';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const CongratulationText = styled.div`
    ${fonts.content};
    color: ${colors.libertyBlue};
    margin-bottom: 50px;
`;

const ButtonContainer = styled.div<{ backgroundColor?: string }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: flex-start;

    ${breakpoints.onlyMobile} {
        align-items: center;
        justify-content: center;
        width: calc(100vw - 44px) !important;
        padding: 12px 0;
        display: flex;
    }
`;

const FullWidthButton = styled(Button)`
    font-size: 19px;
    &:focus-visible {
        outline: none;
    }

     ${breakpoints.onlyMobile} {    
        font-size:16px;
    }
}
`;

const getIsDoneContent = (
    congratulationText: string,
    congratulationTextMobile: string,
    nextText: string,
    domainName: string,
    enterShortcut: string,
    survey_id: any,
    uid: any,
    levels_state: any,
    emailAuth: any,
    width: number,
    onContinueClick: () => void, // Add a callback instead
) => {
    // Select the appropriate text based on screen width
    const textToUse =
        width <= breakpoints.value.mobile
            ? congratulationTextMobile
            : congratulationText;

    return (
        <React.Fragment>
            <CongratulationText
                dangerouslySetInnerHTML={{
                    __html: textToUse.replace('<DOMAIN>', domainName),
                }}
            />
            <ButtonContainer backgroundColor={globalColor.backgroundColor2}>
                <FullWidthButton
                    className="continue-button"
                    buttonType="primary"
                    onClick={onContinueClick} // Use the callback
                >
                    {nextText}
                </FullWidthButton>
            </ButtonContainer>
        </React.Fragment>
    );
};

const getDomainFromUrl = (
    domainId: string,
    domains: Domain[],
): Domain | undefined => {
    if (domainId) {
        let id: Id;
        /**
         * Validate that the domain is indeed a domainID
         */
        try {
            id = validateId(domainId);
        } catch (err) {
            return;
        }

        /**
         * Find the domain in our domains list
         */
        const potentialDomain = domains.find(
            (domain: Domain) => domain.id === id,
        );

        /**
         * If we found it return it
         */
        return potentialDomain;
    }
};

const getCurrentLevelAverage = (domain: Domain) => {
    const a = Math.round(
        domain.subdomains.reduce(
            (acc, subdomain: Subdomain) => acc + (subdomain.level || 0),
            0,
        ) /
            domain.subdomains.filter(
                (subdomain: Subdomain) => subdomain.level != null,
            ).length,
    );

    if (!a) {
        return 0;
    }
    return a;
};

const didUserAnsweredNextQuestion = (
    subdomains: Subdomain[],
    currentSubdomainIndex: number,
) => {
    return (
        subdomains[currentSubdomainIndex] &&
        subdomains[currentSubdomainIndex].level !== null &&
        subdomains[currentSubdomainIndex].level !== undefined
    );
};

const SurveyPage: React.FC = (): React.ReactElement => {
    const dispatch = useDispatch();
    const [isDone, setIsDone] = useState(false);
    const [currentDomain, setCurrentDomain] = useState({
        subdomains: [],
    } as any as Domain);
    const [currentSubdomain, setCurrentSubdomain] = useState(
        {} as any as Subdomain,
    );

    const [isDomainCorrect, setIsDomainCorrect] = useState(true);
    const [currentSubdomainIndex, setCurrentSubdomainIndex] = useState(0);

    const [
        shouldSeeFunctionalApplicationDisclaimer,
        setShouldSeeFunctionalApplicationDisclaimer,
    ] = useState(false);

    // Always call hooks at the top level
    const mandatoryDomains: Domain[] = useSelector(
        (state: State): Domain[] => state.surveyState.domains,
    );
    const optionalDomains: Domain[] = useSelector(
        (state: State): Domain[] => state.optionalSurveyState.domains,
    );

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isReviewMode = searchParams.get('review') === '1';
    const skillClusterType = searchParams.get('type');
    let isMandatoryDomain = false;

    if (
        skillClusterType !== null &&
        +skillClusterType === SkillClusterType.MANDATORY
    ) {
        isMandatoryDomain = true;
    } else if (
        skillClusterType !== null &&
        +skillClusterType === SkillClusterType.OPTIONAL
    ) {
        isMandatoryDomain = false;
    }

    const optionalSelectedDomains = optionalDomains.filter((d) => d.isSelected);

    let emailAuth: any;
    const { width } = useViewport();
    const navigate = useNavigate();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const stateVar = location.state;
    let initialLevelsState: any;
    if (stateVar) {
        if ('levels_state' in stateVar) {
            initialLevelsState = stateVar.levels_state;
        }
        if ('emailAuth' in stateVar) {
            emailAuth = stateVar.emailAuth;
        }
    }
    if (!emailAuth) {
        const storedEmailAuth = sessionStorage.getItem('emailAuth');
        if (storedEmailAuth) {
            emailAuth = storedEmailAuth;
        }
    }
    const { survey_id, uid } = useParams();

    const { t, i18n } = useTranslation(['Survey']);
    const [levels_state, setLevelsState] = useState<any>(initialLevelsState);

    useEffect(() => {
        let cancelled = false;
        async function fetchLevels() {
            const response = await authenticatedGetJson(
                `${backendUrl}/get_levels`,
                survey_id,
            );
            if (!cancelled) {
                setLevelsState(response);
            }
        }
        fetchLevels();
        return () => {
            cancelled = true;
        };
    }, [i18n.language]);

    // Formats a subdomain explanation string so each proficiency level
    // (e.g. "Basic:", "Intermediate:") starts on its own paragraph.
    //
    // The DB may store explanations with <br/> tags, raw newlines, or no
    // separator at all. Rather than guessing the format we:
    //   1. Strip any existing <br> tags and newlines so we start clean.
    //   2. Build an exact regex from the level names returned by /get_levels
    //      (already fetched into levels_state) so matching is reliable.
    //   3. Insert <br><br> before every level label except the first.
    const formatExplanation = (explanation: string): string => {
        // Extract level names from the already-fetched levels_state
        // (e.g. ["None", "Basic", "Intermediate", "Advanced", "Expert"])
        const levelNames: string[] =
            levels_state?.levels_data
                ?.map((l: any) => l.answerLevel)
                .filter(Boolean) || [];

        if (!levelNames.length || !explanation) return explanation;

        // Escape any regex special chars in level names before building the pattern
        const escaped = levelNames
            .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
            .join('|');
        const pattern = new RegExp(`(${escaped}):`, 'g');

        // Normalize: remove all existing <br> variants, newlines, and
        // consecutive spaces so we control the spacing ourselves
        const cleaned = explanation
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/\n+/g, ' ')
            .replace(/\s{2,}/g, ' ')
            .trim();

        // Insert <br><br> before every level label except the very first one,
        // and wrap each label in <strong> so it renders bold
        let firstFound = false;
        return cleaned.replace(pattern, (_match, label) => {
            const bold = `<strong>${label}:</strong>`;
            if (!firstFound) {
                firstFound = true;
                return bold;
            }
            return `<br></n>${bold}`;
        });
    };

    const params = useParams();
    const domainId = params.domainId || '-1';

    const domains = isMandatoryDomain
        ? mandatoryDomains
        : optionalSelectedDomains;

    const answering = useSelector(
        (state: State): boolean => state.surveyState.answerLoading,
    );

    /**
     * Bind last timestamp to re-render on answer if required (edge case)
     */

    const lastTimeStamp: number = useSelector(
        (state: State): number => state.surveyState.userInfos.lastAnsweredTime,
    );

    const averageLevelColor: any = useSelector(
        (state: State): string =>
            state.surveyState.staticDomainAverageColor || '',
    );

    /**
     * Bind app loading and error state
     */
    const { loading, error } = useLoadingAndError();

    /**
     * Effect functions: These will cause side effects
     */

    /**
     * @Effect
     * Change internal current subdomain index
     */

    const effectFunctionSetSubdomainIndexFromCursorChange = (): void => {
        const currentDomain = domains.find(
            (d) => d.id.toString() === domainId.toString(),
        );
        const currentSubDomain = currentDomain?.subdomains.find(
            (sd) =>
                currentDomain.subdomainCursor.toString() === sd.id.toString(),
        );
        if (typeof currentSubDomain?.idx === 'number') {
            setCurrentSubdomainIndex(currentSubDomain.idx + 1);
        }
    };

    /**
     * @Effect
     * Get current domain and set it
     */
    const effectFunctionSetDomain = (): void => {
        const domain = getDomainFromUrl(domainId, domains);
        if (domain) {
            setCurrentDomain(domain);
        } else if (!loading && !error) {
            // If we can't find the domain, redirect
            setIsDomainCorrect(false);
        }
    };

    /**
     * @Effect
     * Get current subdomain and set it
     */

    const effectFunctionSetSubdomain = () => {
        if (currentDomain && currentDomain.subdomains)
            setCurrentSubdomain(
                currentDomain.subdomains[currentSubdomainIndex],
            );
    };

    /**
     * @Effect
     * Check if the domain is done
     */
    const effectFunctionSetIsDone = () => {
        if (
            currentDomain.completed &&
            currentSubdomainIndex >= currentDomain.subdomains.length
        ) {
            setIsDone(true);
        } else {
            setIsDone(false);
        }
    };

    /**
     * @Effect
     * Check if the domain is done
     */
    const effectFunctionSetHasSeenFunctionalApplicationDisclaimer = () => {
        if (currentDomain.mandatory) {
            setShouldSeeFunctionalApplicationDisclaimer(true);
        }
    };

    const answeringRef = useRef(false);

    const onAnswer = async (
        subdomain: Subdomain,
        answerLevel: AnswerLevel,
        survey_id: any,
        uid: any,
        isMandatoryDomain: boolean,
    ) => {
        if (subdomain && Object.keys(subdomain).length && !answering && !answeringRef.current) {
            answeringRef.current = true;
            try {
                await dispatch(
                    answerSubdomain(
                        subdomain,
                        answerLevel,
                        survey_id,
                        uid,
                        isMandatoryDomain,
                    ),
                );
            } finally {
                answeringRef.current = false;
            }
        }
    };

    /**
     * Place the cursor on the next subdomain
     */
    const nextSubdomain = () => {
        if (
            didUserAnsweredNextQuestion(
                currentDomain.subdomains,
                currentSubdomainIndex,
            )
        )
            setCurrentSubdomainIndex(currentSubdomainIndex + 1);
    };

    /**
     * Place the cursor on the previous subdomain
     */
    const previousSubdomain = () => {
        if (currentSubdomainIndex > 0) {
            setCurrentSubdomainIndex(currentSubdomainIndex - 1);
        }
    };

    const getNextFunction = (
        currentIndex: number,
        totalQuestion: number,
        isDone: boolean,
        nextFunction: () => void,
    ) => {
        return (currentIndex === totalQuestion && !isDone) ||
            isDone ||
            !didUserAnsweredNextQuestion(
                currentDomain.subdomains,
                currentSubdomainIndex,
            )
            ? isReviewMode &&
              didUserAnsweredNextQuestion(
                  currentDomain.subdomains,
                  currentSubdomainIndex,
              )
                ? handleContinue
                : undefined
            : nextFunction;
    };

    useEffect(effectFunctionSetSubdomainIndexFromCursorChange, [
        currentDomain,
        lastTimeStamp,
    ]);
    useEffect(effectFunctionSetDomain, [domains, domainId]);
    useEffect(effectFunctionSetSubdomain, [
        currentDomain,
        currentSubdomainIndex,
    ]);
    useEffect(effectFunctionSetIsDone, [currentDomain, currentSubdomainIndex]);
    useEffect(effectFunctionSetHasSeenFunctionalApplicationDisclaimer, [
        currentDomain.id,
    ]);

    // Add this effect to handle cleanup when domain changes

    useEffect(() => {
        // This runs when the domainId changes
        return () => {
            // Reset state when navigating away from this domain
            setIsDone(false);
            setCurrentSubdomainIndex(0);
            setCurrentDomain({ subdomains: [] } as any as Domain);
            setCurrentSubdomain({} as any as Subdomain);
        };
    }, [domainId]);

    // if (loading || error) return <div>Loading or error</div>;
    if (!isDomainCorrect)
        return <Navigate to={`/${survey_id}/${uid}/help`} replace />;
    if (shouldSeeFunctionalApplicationDisclaimer) {
        return (
            <FunctionApplicationsIntroduction
                close={() => setShouldSeeFunctionalApplicationDisclaimer(false)}
            />
        );
    }

    useEffect(() => {
        if (!stateVar && !sessionStorage.getItem('emailAuth')) {
            navigate(`/${survey_id}/${uid}`, { replace: true });
        }
    }, []);

    // Find next incomplete domain (excluding current)
    const nextIncompleteDomain = useMemo(() => {
        if (!currentDomain || !currentDomain.id) return undefined;
        return domains.find((domain) => !domain.completed);
    }, [domains]);

    // Redirect logic after finishing a domain
    useEffect(() => {
        if (isDone && !isReviewMode) {
            // Small delay to ensure state updates have propagated
            const navigationTimeout = setTimeout(() => {
                if (nextIncompleteDomain && nextIncompleteDomain.id) {
                    console.log(
                        nextIncompleteDomain,
                        '------->nextIncompleteDomain',
                    );
                    console.log(currentDomain, '--------->currentDomain');
                    navigate(
                        `/${survey_id}/${uid}/survey/${nextIncompleteDomain.id}?type=${skillClusterType}`,
                        {
                            state: { levels_state, emailAuth },
                            replace: true,
                        },
                    );
                } else {
                    navigate(`/${survey_id}/${uid}/mandatory-welcome`, {
                        state: { levels_state, emailAuth },
                        replace: true,
                    });
                }
            }, 100); // Small delay of 50ms

            // Only have one return statement in the cleanup function
            return () => clearTimeout(navigationTimeout);
        } else if (isDone && isReviewMode) {
            // Small delay to ensure state updates have propagated
            const navigationTimeout = setTimeout(() => {
                navigate(`/${survey_id}/${uid}/mandatory-welcome`, {
                    state: { levels_state, emailAuth },
                    replace: true,
                });
            }, 100); // Small delay of 50ms

            // Only have one return statement in the cleanup function
            return () => clearTimeout(navigationTimeout);
        }
        // No early return here - let the effect complete normally
    }, [
        isDone,
        nextIncompleteDomain,
        navigate,
        survey_id,
        uid,
        levels_state,
        emailAuth,
        domains,
        isReviewMode, // Added isReviewMode as dependency
    ]);

    // Add this function at the component level where hooks are allowed
    const handleContinue = () => {
        navigate(`/${survey_id}/${uid}/mandatory-welcome`, {
            state: { levels_state, emailAuth },
        });
    };

    if (!stateVar && !sessionStorage.getItem('emailAuth')) {
        return <></>;
    }

    if (!levels_state) {
        return <></>;
    }

    return (
        <SurveyPageTemplateRoleBased
            isReviewMode={isReviewMode}
            showFinishSurveyButton={!isMandatoryDomain}
            title={currentDomain.title}
            doneText={isDone ? t('doneText') || '' : undefined}
            helpText={t('helpText') || ''}
            logoImage="/talentbuilderlogo.png"
            questionPhrase={
                currentDomain.mandatory
                    ? t('mandatoryQuestionPhrase')
                    : t('questionPhrase')
            }
            questionDomain={currentSubdomain && currentSubdomain.title}
            questionDomainExplanation={
                currentSubdomain && currentSubdomain.explanation
            }
            targetSample={
                currentSubdomain &&
                formatExplanation(currentSubdomain.explanation)
            }
            progress={
                currentSubdomainIndex + 1 > currentDomain.subdomains.length
                    ? currentDomain.subdomains.length
                    : currentSubdomainIndex + 1
            }
            progressTotal={currentDomain.subdomains.length}
            nextClick={getNextFunction(
                currentSubdomainIndex,
                isReviewMode
                    ? currentDomain.subdomains.length
                    : currentDomain.subdomains.length - 1,
                isDone,
                () => nextSubdomain(),
            )}
            backClick={
                currentSubdomainIndex > 0
                    ? () => previousSubdomain()
                    : undefined
            }
            illustrationSideContent={
                <SurveyIllustationProgress
                    image={domainMapping[currentDomain.idx]}
                    centerText={currentDomain.title}
                    progress={currentSubdomainIndex}
                    total={currentDomain.subdomains.length}
                    color={
                        averageLevelColor
                            ? averageLevelColor
                            : levels_state?.levels_data?.[
                                  getCurrentLevelAverage(currentDomain)
                              ]?.color || ''
                    }
                />
            }
            shouldShowTooltip={false}
        >
            {isDone ? (
                !isReviewMode ? (
                    <></> // Empty fragment when in review mode
                ) : (
                    getIsDoneContent(
                        t('congratulationText'),
                        t('congratulationTextMobile'),
                        t('nextText'),
                        currentDomain.title,
                        t('enter'),
                        survey_id,
                        uid,
                        levels_state,
                        emailAuth,
                        width,
                        handleContinue, // Pass the handler function
                    )
                )
            ) : (
                <Answers
                    loading={loading}
                    levels_state={levels_state}
                    survey_id={survey_id}
                    previousAnswer={currentSubdomain && currentSubdomain.level}
                    onAnswer={(answerLevel: AnswerLevel) =>
                        onAnswer(
                            currentSubdomain,
                            answerLevel,
                            survey_id,
                            uid,
                            isMandatoryDomain,
                        )
                    }
                />
            )}
        </SurveyPageTemplateRoleBased>
    );
};

export default SurveyPage;
